import { axiosService } from '@/services/AxiosService';
import { useToast } from 'vue-toastification';

/**
 * @typedef {Object} Box
 * @property {number} id
 * @property {string} name
 * @property {boolean} truciolo
 * @property {number} box_type_id
 * @property {number} horse_stable_id
 * @property {string} created
 * @property {string} modified
 * @property {HorseStable} horse_stable
 */

/**
 * @typedef {Object} HorseStable
 * @property {number} id
 * @property {string|null} name
 * @property {string} created
 * @property {string} modified
 * @property {number} riding_stable_id
 */

/**
 * @typedef {Object} BoxType
 * @property {number} id
 * @property {name} string
 */

/**
 * @typedef {Object} HorseStableJoinData
 * @property {number} box_id
 * @property {number} horse_stable_id
 */

/**
 * @typedef {Object} BoxTypeJoinData
 * @property {number} box_type_id
 * @property {number} type_id
 */

class BoxesService {
	getPath = '/boxes/get?horse_stable_id=';
	getTypesPath = 'boxes/get-types';
	postPath = '/boxes/save';
	delPath = '/boxes/delete';
	toast = useToast();
	boxes = [];
	box_types = [];

	constructor() {
		this.axiosInstance = axiosService.getInstance(); // Usa l'istanza di AxiosService
	}

	/**
	 * Ottieni la lista dei box per la scuderia.
	 * @returns {Promise<Box[]>} I dati dei box.
	 * @throws {Error} Se la richiesta fallisce.
	 */
	async getBoxes(page, search = '', params = {}, length = 10) {
		await this.getBoxesType();
		try {
			const response = await this.axiosInstance.get(
				this.getPath + params.horse_stable_id,
				{ page, length, search }
			);

			if (response.status !== 200) {
				throw new Error('Failed to fetch boxes info');
			}

			/** @type {Box[]} */
			// console.log('BoxesResponse ---> ', response);
			// AGGIUNGI ALL'OGGETTO BOX IL NOME DEL SUO TIPO DI BOX E CONVERTI IN BOOLEAN IL TRUCIOLO
			let boxesWithJoinedData = [];
			response.data.boxes.forEach((box) => {
				box.box_type_name = this.box_types.find(
					(type) => type.id == box.box_type_id
				).name;
				boxesWithJoinedData.push(box);
			});
			const boxesData = { data: boxesWithJoinedData };
			this.boxes = response.data.boxes;
			return boxesData;
		} catch (error) {
			console.error('Error fetching boxes info:', error);
			throw error; // Rialza l'errore per gestirlo nel chiamante
		}
	}

	getBoxById(box_id) {
		const result = this.boxes.find((box) => box.id === box_id);
		return result;
	}

	/**
	 * Ottieni la lista dei tipi di box.
	 * @returns {Promise<BoxType[]>} I dati dei tipi di box.
	 * @throws {Error} Se la richiesta fallisce.
	 */
	async getBoxesType() {
		try {
			const response = await this.axiosInstance.get(this.getTypesPath);
			if (response.status !== 200) {
				throw new Error('Failed to fetch boxes info');
			}

			/** @type {BoxType[]} */
			// console.log('BoxTypeResponse ---> ', response);
			const boxTypesData = response.data.types;
			this.box_types = boxTypesData;
			return boxTypesData;
		} catch (error) {
			console.error('Error fetching type info:', error);
			throw error; // Rialza l'errore per gestirlo nel chiamante
		}
	}

	/**
	 * Crea/modifica il box del horse stable.
	 * @returns {Promise<string>} Messaggio di conferma con ID.
	 * @throws {Error} Se la richiesta fallisce.
	 */
	async saveBox(horseStable_id, box) {
		try {
			const boxData = {
				name: box.name,
				horse_stable_id: box.horse_stable_id ?? horseStable_id,
				box_type_id: box.box_type_id,
				truciolo: box.truciolo,
			};
			if (box.id) {
				boxData.id = box.id;
			}
			console.log(boxData);

			const response = await this.axiosInstance.post(this.postPath, boxData);

			if (response.status !== 200) {
				throw new Error('Failed to create member');
			}

			/** @type {string} */
			// console.log('messageResponse ---> ', response);
			// const messageResponse = response.data.message;
			// this.toast.success(messageResponse);
			return response;
		} catch (error) {
			this.toast.error(error);
			console.error('Error adding member:', error);
			throw error; // Rialza l'errore per gestirlo nel chiamante
		}
	}

	/**
	 * Elimina il box dalla scuderia.
	 * @returns {Promise<string>} Messaggio di conferma.
	 * @throws {Error} Se la richiesta fallisce.
	 */
	async deleteBox(box_id) {
		try {
			const response = await this.axiosInstance.delete(this.delPath, {
				data: {
					id: box_id,
				},
			});

			if (response.status !== 200) {
				throw new Error('Failed to delete member');
			}

			/** @type {string} */
			// console.log('messageResponse ---> ', response);
			const messageResponse = response.data.message;
			this.toast.success(messageResponse);
			return messageResponse;
		} catch (error) {
			this.toast.error(error);
			console.error('Error deleting member:', error);
			throw error; // Rialza l'errore per gestirlo nel chiamante
		}
	}
}

export const boxesService = new BoxesService();
